/**
 * TSX 페이지 소스 → JS 컴파일.
 * esbuild-wasm 을 메인 컨텍스트에서 단일 인스턴스로 init. 페이지마다 transform 호출.
 *
 * Vite 가 esbuild.wasm 파일을 정적 자산으로 자동 복사 (?url import).
 */
import { initialize, transform } from 'esbuild-wasm';
import esbuildWasmUrl from 'esbuild-wasm/esbuild.wasm?url';

let initPromise: Promise<void> | null = null;

async function ensureInitialized(): Promise<void> {
  if (!initPromise) {
    initPromise = initialize({ wasmURL: esbuildWasmUrl, worker: false }).catch((err) => {
      // 다음 시도에서 재시도 가능하게 promise 초기화.
      initPromise = null;
      throw err;
    });
  }
  return initPromise;
}

export interface TsxCompileResult {
  ok: true;
  code: string;
}
export interface TsxCompileError {
  ok: false;
  error: string;
}

/**
 * TSX 소스를 ES2020 IIFE 로 컴파일.
 * - JSX → React.createElement
 * - export default → globalThis.__SLIDE_PAGE__ 으로 노출
 * - import 문은 빈 객체로 (사용자가 import 쓰면 컴파일은 통과하지만 런타임에 undefined).
 */
export async function compileTsx(source: string): Promise<TsxCompileResult | TsxCompileError> {
  try {
    await ensureInitialized();
    // export default Foo  →  globalThis.__SLIDE_PAGE__ = Foo (단순 패턴 변환).
    const normalized = source
      .replace(/^\s*import\s+[\s\S]*?from\s+['"][^'"]+['"];?\s*$/gm, '')
      .replace(/^\s*export\s+default\s+function\s+([A-Za-z_$][\w$]*)/m, 'function $1')
      .replace(/^\s*export\s+default\s+/m, 'const __SLIDE_PAGE_DEFAULT__ = ');

    // export default function Foo() {} 케이스에는 별도 라인으로 등록.
    const exportMarker = /^\s*export\s+default\s+/m.test(source)
      ? ''
      : '\nglobalThis.__SLIDE_PAGE__ = (typeof __SLIDE_PAGE_DEFAULT__ !== "undefined") ? __SLIDE_PAGE_DEFAULT__ : null;';

    // 사용자가 export default function Page() {} 형태 → Page 가 var, 등록 라인 별도.
    const fnNameMatch = source.match(/^\s*export\s+default\s+function\s+([A-Za-z_$][\w$]*)/m);
    const registerLine = fnNameMatch
      ? `\nglobalThis.__SLIDE_PAGE__ = ${fnNameMatch[1]};`
      : exportMarker;

    const result = await transform(normalized + registerLine, {
      loader: 'tsx',
      target: 'es2020',
      format: 'iife',
      jsx: 'transform',
      jsxFactory: 'React.createElement',
      jsxFragment: 'React.Fragment',
      logLevel: 'silent',
    });
    return { ok: true, code: result.code };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, error: message };
  }
}
